"use client";

import React, { useEffect, useRef, useState } from "react";

import { OspToast, deviceDetector } from "@ospweb/sdk-ui";
import type { Web3LoginSupportedType } from "@/src/features/auth/compat/sdk-types";
import log from "logbeacon";
import { useParams } from "next/navigation";
import { ILoginModalProps } from "./types";
import { agreement } from "@/src/const";
import Translations from "@translate/components/modal/LoginModal";
import { I18nUtil } from "@utils/i18n";
import { useIsPc } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

export const LoginModal = (props: ILoginModalProps) => {
  const {
    visable,
    closeModal,
    onClickLogin,
    loading,
    customStyles,
    hideCloseButton,
    mode,
    className,
  } = props;

  const { locale } = useParams();
  const [isPc] = useIsPc();
  const [loginType, setLoginType] = useState<
    Web3LoginSupportedType | undefined
  >(undefined);
  const modalRef = useRef<LoginModalElement>(null);

  useEffect(() => {
    if (!modalRef.current) {
      return;
    }
    const isOpen = modalRef.current?.isOpen;
    if (visable && !isOpen) {
      modalRef.current.show();
      log.trace(`[GTM_VIEW][view_login_modal]`, { source: "default" });
    } else if (!visable && isOpen) {
      modalRef.current.close();
    }
  }, [visable]);

  // 应用自定义样式
  useEffect(() => {
    if (!modalRef.current) {
      return;
    }

    // 应用传入的自定义样式
    if (customStyles) {
      Object.entries(customStyles).forEach(([property, value]) => {
        modalRef.current?.style.setProperty(property, String(value));
      });
    }

    // 控制关闭按钮的显示 - 使用定时器确保DOM已渲染
    const hideCloseButtonTimeout = setTimeout(() => {
      if (modalRef.current?.shadowRoot) {
        const closeButton = modalRef.current.shadowRoot.querySelector(
          ".close-button",
        ) as HTMLElement;
        if (closeButton) {
          closeButton.style.display = hideCloseButton ? "none" : "flex";
        }
      }
    }, 0);

    return () => clearTimeout(hideCloseButtonTimeout);
  }, [customStyles, hideCloseButton]);

  useEffect(() => {
    if (!modalRef.current) {
      return;
    }
    const modal = modalRef.current;
    const login = (
      event: CustomEvent<LoginEventDetail<Web3LoginSupportedType>>,
    ) => {
      log.info("[login][trace][LoginModal] event", event);
      if (!event.detail.method) {
        return;
      }
      if (!modal.isAgree) {
        OspToast.error({
          content: I18n.t("Please agree to the Privacy Policy first"),
        });
        return;
      }
      const source = mode === "inline" ? "fullscreen" : "default";
      log.trace("[GTM_CLICK][click_login_button]", {
        login_type: event.detail.method,
        source,
      });
      setLoginType(event.detail.method);
      onClickLogin(event.detail.method);
    };
    modal.addEventListener("close", closeModal);
    modal.addEventListener("login", login);
    return () => {
      modal.removeEventListener("close", closeModal);
      modal.removeEventListener("login", login);
    };
  }, [closeModal, onClickLogin, mode]);

  useEffect(() => {
    try {
      if (!modalRef.current) {
        return;
      }
      if (deviceDetector.isIOSDevice()) {
        modalRef.current?.setLoginMethods(["APPLE", "GOOGLE"]);
      } else {
        modalRef.current?.setLoginMethods(["GOOGLE", "APPLE"]);
      }
      modalRef.current?.setMoreLoginMethods?.([]);
    } catch (error) {
      log.error("[LoginModal] useEffect setLoginMethods error", error);
    }
    if (mode === "inline") {
      log.trace(`[GTM_VIEW][view_login_modal]`, { source: "fullscreen" });
      return;
    }
  }, []);

  useEffect(() => {
    if (!modalRef.current) {
      return;
    }
    const isOpen = modalRef.current?.isOpen;
    if (!isOpen) {
      return;
    }
    if (loading) {
      modalRef.current?.setLoading<Web3LoginSupportedType>(loginType);
    } else {
      modalRef.current?.setLoading(null);
    }
  }, [loading, loginType]);
  useEffect(() => {
    if (!modalRef.current) {
      return;
    }
    modalRef.current?.setTexts({
      title: isPc ? I18n.t("Login / Sign up") : I18n.t("Welcome to Edgen"),
      or: I18n.t("or"),
      terms: I18n.t("I accept the"),
      termsOfUse: I18n.t("Terms of Use"),
      privacyPolicy: I18n.t("Privacy Policy"),
      APPLE: I18n.t("Continue with Apple"),
      GOOGLE: I18n.t("Continue with Google"),
      creditsMessage: I18n.t("Sign up & get 3 latest Alpha Signals"),
    });
  }, [locale, isPc]);

  return (
    <login-modal
      ref={modalRef}
      mode={mode}
      terms-of-use-link={agreement.termsLink}
      privacy-policy-link={agreement.privacyLink}
      className={className}
    />
  );
};
